import React, { useEffect, useMemo, useRef, useState } from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import SyncIcon from "@material-ui/icons/Sync";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import { Button, Grid, Card, CardContent, CircularProgress, Typography, TextField } from "@material-ui/core";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Map as LeafletMap, Marker, Popup, Polyline, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { waymarkerApi } from "../../api/waymarker";
import { hasExactPrivilege, hasPrivileges } from "../../util/auth";

L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const formatWaymarker = (wm) => {
    const lat = toNumber(wm.latitude);
    const lon = toNumber(wm.longitude);
    return {
        waymarker_id: wm.id,
        waymarker_type: wm.type ?? "",
        created_at: wm.created_at ?? "",
        race_name: wm.race_name ?? "",
        race_stage_index: wm.race_stage_index ?? "",
        distance_to_finish: wm.distance_to_finish ?? "",
        description: wm.description ?? "",
        url: wm.url ?? "",
        race_stage_id: wm.race_stage_id ?? null,
        incident_id: wm.incident_id ?? null,
        latitude: lat,
        longitude: lon,
    };
};

const parseGpxPoints = async (file) => {
    if (!file) return [];
    const text = await file.text();
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, "application/xml");
    const points = [];

    xml.querySelectorAll("trkpt,rtept").forEach((node) => {
        const lat = toNumber(node.getAttribute("lat"));
        const lon = toNumber(node.getAttribute("lon"));
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
            points.push([lat, lon]);
        }
    });

    return points;
};

const WAYMARKER_COLOR_PALETTE = [
    "#e53935",
    "#1e88e5",
    "#43a047",
    "#fb8c00",
    "#8e24aa",
    "#3949ab",
    "#00897b",
    "#6d4c41",
];

const WAYMARKER_TYPE_COLORS = {
    incident: "#e53935",
    safer: "#1e88e5",
    nearmiss: "#8e24aa",
};

const getTypeKey = (value) => {
    const trimmed = String(value || "").trim();
    return trimmed.length ? trimmed : "Unknown";
};

const getTypeColor = (typeKey) => {
    const normalized = typeKey.toLowerCase();
    if (WAYMARKER_TYPE_COLORS[normalized]) {
        return WAYMARKER_TYPE_COLORS[normalized];
    }

    let hash = 0;
    for (let i = 0; i < typeKey.length; i += 1) {
        hash = (hash << 5) - hash + typeKey.charCodeAt(i);
        hash |= 0;
    }
    const index = Math.abs(hash) % WAYMARKER_COLOR_PALETTE.length;
    return WAYMARKER_COLOR_PALETTE[index];
};

const createTypeIcon = (color) => {
    const html = `<div style="background:${color};width:14px;height:14px;border:2px solid #ffffff;border-radius:50%;box-shadow:0 0 2px rgba(0,0,0,0.6);"></div>`;
    return L.divIcon({
        html,
        className: "",
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });
};

const isTextSelection = () => {
    const selection = window.getSelection && window.getSelection();
    return selection && selection.toString().trim().length > 0;
};

const INCIDENT_LINK_BUTTON_STYLE = {
    minWidth: 44,
    paddingRight: 18,
    position: "relative",
};

const INCIDENT_LINK_CORNER_STYLE = {
    alignItems: "center",
    background: "#ffffff",
    borderRadius: "50%",
    display: "flex",
    height: 16,
    justifyContent: "center",
    position: "absolute",
    right: -5,
    top: -6,
    width: 16,
};

export default function WaymarkerOverview() {
    const mapRef = useRef(null);
    const [raceStageId, setRaceStageId] = useState("");
    const [incidentId, setIncidentId] = useState("");
    const [distanceM, setDistanceM] = useState("");
    const [gpxFile, setGpxFile] = useState(null);
    const [gpxTrack, setGpxTrack] = useState([]);
    const [waymarkers, setWaymarkers] = useState([]);
    const [selectedWaymarkerId, setSelectedWaymarkerId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingSync, setLoadingSync] = useState(false);
    const [deletingWaymarkerIds, setDeletingWaymarkerIds] = useState([]);
    const [updatingIncidentLinkWaymarkerIds, setUpdatingIncidentLinkWaymarkerIds] = useState([]);
    const [message, setMessage] = useState("");

    const handleForceSync = async () => {
        setLoadingSync(true);
        await waymarkerApi.syncWaymarkers();
        setLoadingSync(false);
        window.location.reload();
    }

    const deleteWaymarker = async (waymarkerId) => {
        try {
            setDeletingWaymarkerIds((ids) => [...ids, waymarkerId]);
            setMessage("");
            await waymarkerApi.deleteWaymarker(waymarkerId);
            setWaymarkers((items) => items.filter((wm) => wm.waymarker_id !== waymarkerId));
            setSelectedWaymarkerId((selectedId) => selectedId === waymarkerId ? null : selectedId);
            setMessage(`Successfully deleted waymarker ${waymarkerId}.`);
        } catch (err) {
            console.error("Error deleting waymarker", err);
            const apiMessage = err?.response?.data?.message;
            setMessage(apiMessage || `Failed to delete waymarker ${waymarkerId}.`);
        } finally {
            setDeletingWaymarkerIds((ids) => ids.filter((id) => id !== waymarkerId));
        }
    };

    const confirmWaymarkerDelete = (waymarkerId) => {
        confirmAlert({
            title: "Confirm to submit",
            message: `Are you sure you want to delete the waymarker ${waymarkerId}?`,
            buttons: [
                {
                    label: "Yes",
                    onClick: () => deleteWaymarker(waymarkerId),
                },
                {
                    label: "No",
                    onClick: () => setMessage(""),
                },
            ],
        });
    };

    const fetchWaymarkers = async () => {
        try {
            setLoading(true);
            setMessage("");
            const data = await waymarkerApi.getWaymarkers({
                raceStageId,
                incidentId,
                distanceM,
                gpxFile,
            });
            const list = Array.isArray(data) ? data.map(formatWaymarker) : [];
            setWaymarkers(list);
            if (!Array.isArray(data)) {
                setMessage("Unexpected response from server - expected array.");
            }
        } catch (err) {
            console.error("Error fetching waymarkers", err);
            const apiMessage = err?.response?.data?.message;
            setMessage(apiMessage || "Failed to load waymarkers.");
            setWaymarkers([]);
        } finally {
            setLoading(false);
        }
    };

    const isUpdatingIncidentLink = (waymarkerId) =>
        updatingIncidentLinkWaymarkerIds.includes(waymarkerId);

    const setIncidentLinkUpdating = (waymarkerId, updating) => {
        setUpdatingIncidentLinkWaymarkerIds((ids) => {
            if (updating) {
                return ids.includes(waymarkerId) ? ids : [...ids, waymarkerId];
            }
            return ids.filter((id) => id !== waymarkerId);
        });
    };

    const linkWaymarkerToIncident = async (waymarkerId, newIncidentId) => {
        const parsedIncidentId = Number.parseInt(newIncidentId, 10);
        if (!Number.isInteger(parsedIncidentId) || parsedIncidentId <= 0) {
            setMessage("Enter a valid incident ID.");
            return;
        }

        try {
            setIncidentLinkUpdating(waymarkerId, true);
            setMessage("");
            await waymarkerApi.linkWaymarkerToIncident(waymarkerId, parsedIncidentId);
            await fetchWaymarkers();
            setMessage(`Successfully linked waymarker ${waymarkerId} to incident ${parsedIncidentId}.`);
        } catch (err) {
            console.error("Error linking waymarker to incident", err);
            const apiMessage = err?.response?.data?.message;
            setMessage(apiMessage || `Failed to link waymarker ${waymarkerId} to incident ${parsedIncidentId}.`);
        } finally {
            setIncidentLinkUpdating(waymarkerId, false);
        }
    };

    const unlinkWaymarkerFromIncident = async (waymarkerId, linkedIncidentId) => {
        try {
            setIncidentLinkUpdating(waymarkerId, true);
            setMessage("");
            await waymarkerApi.unlinkWaymarkerFromIncident(waymarkerId);
            await fetchWaymarkers();
            setMessage(`Successfully unlinked waymarker ${waymarkerId} from incident ${linkedIncidentId}.`);
        } catch (err) {
            console.error("Error unlinking waymarker from incident", err);
            const apiMessage = err?.response?.data?.message;
            setMessage(apiMessage || `Failed to unlink waymarker ${waymarkerId} from incident ${linkedIncidentId}.`);
        } finally {
            setIncidentLinkUpdating(waymarkerId, false);
        }
    };

    const confirmWaymarkerIncidentUnlink = (rowData) => {
        confirmAlert({
            title: "Unlink incident?",
            message: `Unlink waymarker ${rowData.waymarker_id} from incident ${rowData.incident_id}?`,
            buttons: [
                {
                    label: "Unlink",
                    onClick: () => unlinkWaymarkerFromIncident(rowData.waymarker_id, rowData.incident_id),
                },
                {
                    label: "Cancel",
                    onClick: () => setMessage(""),
                },
            ],
        });
    };

    const promptWaymarkerIncidentLink = (rowData) => {
        let incidentIdInput = "";
        confirmAlert({
            customUI: ({ onClose }) => (
                <div className="react-confirm-alert-body">
                    <h1>Link incident</h1>
                    <p>Link waymarker {rowData.waymarker_id} to an incident without a waymarker.</p>
                    <TextField
                        label="Incident ID"
                        type="number"
                        variant="outlined"
                        size="small"
                        autoFocus
                        fullWidth
                        onChange={(event) => {
                            incidentIdInput = event.target.value;
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                onClose();
                                linkWaymarkerToIncident(rowData.waymarker_id, incidentIdInput);
                            }
                        }}
                    />
                    <div className="react-confirm-alert-button-group">
                        <button
                            type="button"
                            onClick={() => {
                                onClose();
                                linkWaymarkerToIncident(rowData.waymarker_id, incidentIdInput);
                            }}
                        >
                            Link
                        </button>
                        <button type="button" onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                </div>
            ),
        });
    };

    useEffect(() => {
        fetchWaymarkers();
    }, []);

    useEffect(() => {
        let mounted = true;

        if (!gpxFile) {
            setGpxTrack([]);
            return undefined;
        }

        parseGpxPoints(gpxFile)
            .then((points) => {
                if (mounted) setGpxTrack(points);
            })
            .catch((err) => {
                console.error("Failed to parse GPX", err);
                if (mounted) setGpxTrack([]);
            });

        return () => {
            mounted = false;
        };
    }, [gpxFile]);

    const tableData = useMemo(() => waymarkers, [waymarkers]);
    const mappableWaymarkers = useMemo(
        () => waymarkers.filter((wm) => Number.isFinite(wm.latitude) && Number.isFinite(wm.longitude)),
        [waymarkers]
    );
    const markerPositions = useMemo(
        () => mappableWaymarkers.map((wm) => [wm.latitude, wm.longitude]),
        [mappableWaymarkers]
    );
    const mapPositions = useMemo(() => {
        if (gpxTrack.length) {
            return [...markerPositions, ...gpxTrack];
        }
        return markerPositions;
    }, [markerPositions, gpxTrack]);
    const bounds = useMemo(() => {
        if (!mapPositions.length) return null;
        return L.latLngBounds(mapPositions);
    }, [mapPositions]);
    const mapCenter = useMemo(() => {
        if (mapPositions.length) return mapPositions[0];
        return [0, 0];
    }, [mapPositions]);

    const legendItems = useMemo(() => {
        const typeKeys = new Set();
        waymarkers.forEach((wm) => typeKeys.add(getTypeKey(wm.waymarker_type)));
        return Array.from(typeKeys).sort().map((typeKey) => ({
            typeKey,
            color: getTypeColor(typeKey),
        }));
    }, [waymarkers]);

    const typeIconCache = useMemo(() => {
        const cache = new Map();
        legendItems.forEach(({ typeKey, color }) => {
            cache.set(typeKey, createTypeIcon(color));
        });
        return cache;
    }, [legendItems]);

    useEffect(() => {
        if (!bounds || !mapRef.current) return;
        mapRef.current.leafletElement.fitBounds(bounds, { padding: [24, 24] });
    }, [bounds]);

    const shouldIgnoreRowClick = (event) => {
        if (isTextSelection()) return true;
        const target = event.target;
        if (!target || !target.closest) return false;
        return Boolean(target.closest("a,button,input,textarea,select,label"));
    };

    const handleRowClick = (event, rowData) => {
        if (shouldIgnoreRowClick(event)) return;
        if (!rowData) return;
        const { latitude, longitude, waymarker_id } = rowData;
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
        if (selectedWaymarkerId === waymarker_id) {
            setSelectedWaymarkerId(null);
            return;
        }
        setSelectedWaymarkerId(waymarker_id);
        if (!mapRef.current) return;
        mapRef.current.leafletElement.setView([latitude, longitude], 14, { animate: true });
    };

    const stopRowClick = (event) => {
        event.stopPropagation();
    };

    return (
        <div key="waymarker-overview">
            <h1>Waymarkers Overview</h1>

            <Card style={{ marginBottom: 16 }}>
                <CardContent>
                    <Typography variant="h6" style={{ marginBottom: 8 }}>
                        Waymarkers Map
                    </Typography>
            <Grid container spacing={2} alignItems="flex-start" style={{ marginBottom: 12 }}>
                <Grid item>
                    <TextField
                        label="Distance (m)"
                        value={distanceM}
                        onChange={(e) => setDistanceM(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item>
                    <Button
                        //variant="contained"
                        //color="primary"
                        color='default'
                        variant='outlined'
                        onClick={fetchWaymarkers}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Apply Filters"}
                    </Button>
                </Grid>
                <Grid item>
                    <input
                        accept=".gpx"
                        id="gpx-upload"
                        type="file"
                        onChange={(e) => setGpxFile(e.target.files?.[0] ?? null)}
                        style={{ display: "none" }}
                    />
                    <label htmlFor="gpx-upload">
                        <Button variant="outlined" color="default" component="span">
                            {gpxFile ? gpxFile.name : "Upload GPX"}
                        </Button>
                    </label>
                </Grid>
                <Grid item>
                    <Button
                        variant="text"
                        color="default"
                        onClick={() => setGpxFile(null)}
                        disabled={!gpxFile || loading}
                    >
                        Clear GPX
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    {message && <Typography color="error">{message}</Typography>}
                </Grid>
            </Grid>
                    {legendItems.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                            {legendItems.map((item) => (
                                <div key={item.typeKey} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span
                                        style={{
                                            display: "inline-block",
                                            width: 12,
                                            height: 12,
                                            borderRadius: "50%",
                                            background: item.color,
                                            border: "2px solid #ffffff",
                                            boxShadow: "0 0 2px rgba(0,0,0,0.6)",
                                        }}
                                    />
                                    <span>{item.typeKey}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {mappableWaymarkers.length === 0 ? (
                        <Typography color="textSecondary">
                            No waymarkers with coordinates available.
                        </Typography>
                    ) : (
                        <div style={{ height: 420, width: "100%" }}>
                            <LeafletMap ref={mapRef} center={mapCenter} zoom={10} style={{ height: "100%", width: "100%" }}>
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                {gpxTrack.length > 1 && (
                                    <Polyline positions={gpxTrack} color="#e53935" />
                                )}
                                {mappableWaymarkers.map((wm) => {
                                    const typeKey = getTypeKey(wm.waymarker_type);
                                    const icon = typeIconCache.get(typeKey);
                                    
                                    return (
                                    <Marker
                                        key={wm.waymarker_id}
                                        position={[wm.latitude, wm.longitude]}
                                        icon={icon}
                                        onClick={() =>
                                            setSelectedWaymarkerId((prev) =>
                                                prev === wm.waymarker_id ? null : wm.waymarker_id
                                            )
                                        }
                                    >
                                        <Popup onClose={() => setSelectedWaymarkerId(null)}>
                                            <div style={{ minWidth: 180 }}>
                                                <strong>{wm.waymarker_type || "Waymarker"}</strong>
                                                <div>{wm.description || "No description"}</div>
                                                {wm.distance_to_finish && (
                                                    <div>Distance: {wm.distance_to_finish} km</div>
                                                )}
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                                })}
                            </LeafletMap>
                        </div>
                    )}
                </CardContent>
            </Card>

            <MaterialTable
                title={null}
                columns={[
                    //{ title: "ID", field: "waymarker_id", type: "numeric" },  
                    { title: "Created At", field: "created_at", render: (rowData) => new Date(rowData.created_at).toLocaleString(),
                        customFilterAndSearch: (search, rowData) => {
                            if (!rowData.created_at) return false;
                            const createdAtString = new Date(rowData.created_at).toLocaleString();
                            return createdAtString.toLowerCase().includes(search.toLowerCase());
                        }
                    },
                    { title: "Type", field: "waymarker_type" },
                    { title: "Race", field: "race_name" },
                    { title: "Stage", field: "race_stage_index", type: "numeric" },
                    { title: "Distance to finish (km)", field: "distance_to_finish", type: "numeric" },
                    { title: "Description", field: "description" },
                    {
                        title: "Links",
                        field: "links",
                        render: (rowData) => {
                            const updatingIncidentLink = isUpdatingIncidentLink(rowData.waymarker_id);
                            return (
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                    <Button
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        component="a"
                                        href={rowData.url || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={stopRowClick}
                                    >
                                        URL
                                    </Button>
                                    <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        onClick={(event) => {
                                            stopRowClick(event);
                                            if (rowData.race_stage_id) window.open(`/#/race/${rowData.race_stage_id}`, '_blank');
                                        }}
                                        disabled={!rowData.race_stage_id}
                                    >
                                        R {rowData.race_stage_id ?? "-"}
                                    </Button>
                                    {rowData.incident_id ? (
                                        <Button
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            style={INCIDENT_LINK_BUTTON_STYLE}
                                            onClick={(event) => {
                                                stopRowClick(event);
                                                window.open(`/#/incident/${rowData.incident_id}`, '_blank');
                                            }}
                                            disabled={updatingIncidentLink}
                                        >
                                            I {rowData.incident_id}
                                            <span
                                                title="Unlink incident"
                                                style={INCIDENT_LINK_CORNER_STYLE}
                                                onClick={(event) => {
                                                    stopRowClick(event);
                                                    event.preventDefault();
                                                    confirmWaymarkerIncidentUnlink(rowData);
                                                }}
                                            >
                                                {updatingIncidentLink ? (
                                                    <CircularProgress size={12} />
                                                ) : (
                                                    <CloseIcon style={{ fontSize: 13 }} />
                                                )}
                                            </span>
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            color="default"
                                            variant="outlined"
                                            style={INCIDENT_LINK_BUTTON_STYLE}
                                            onClick={(event) => {
                                                stopRowClick(event);
                                                promptWaymarkerIncidentLink(rowData);
                                            }}
                                            disabled={updatingIncidentLink}
                                        >
                                            I -
                                            <span title="Link incident" style={INCIDENT_LINK_CORNER_STYLE}>
                                                {updatingIncidentLink ? (
                                                    <CircularProgress size={12} />
                                                ) : (
                                                    <AddIcon style={{ fontSize: 13 }} />
                                                )}
                                            </span>
                                        </Button>
                                    )}
                                </div>
                            );
                        },
                    },
                ]}
                data={
                    selectedWaymarkerId
                        ? tableData.filter((row) => row.waymarker_id === selectedWaymarkerId)
                        : tableData
                }
                onRowClick={handleRowClick}
                options={{
                    sorting: true,
                    filtering: true,
                    search: false,
                    paging: true,
                    pageSize: 50,
                    pageSizeOptions: [50, 100],
                    exportButton: false,
                    actionsColumnIndex: -1,
                    rowStyle: (rowData) => ({
                        backgroundColor: rowData.waymarker_id === selectedWaymarkerId ? "#ffe9a8" : "inherit",
                    }),
                }}
                actions={[
                    (rowData) => ({
                        icon: () =>
                            deletingWaymarkerIds.includes(rowData.waymarker_id) ? (
                                <CircularProgress size={20} />
                            ) : (
                                <DeleteForeverIcon />
                            ),
                        tooltip: "Delete waymarker",
                        onClick: (event, rowData) => {
                            stopRowClick(event);
                            confirmWaymarkerDelete(rowData.waymarker_id);
                        },
                        disabled: deletingWaymarkerIds.includes(rowData.waymarker_id),
                        hidden: !hasPrivileges("commissaire"),
                    }),
                    {
                        icon: () =>
                            loadingSync ? (
                                <CircularProgress size={20} />
                            ) : (
                                <SyncIcon />
                            ),
                        tooltip: "Force Sync waymarkers",
                        isFreeAction: true, // puts button in toolbar
                        onClick: handleForceSync,
                        hidden: !hasExactPrivilege('admin')
                    },
                ]}
                components={{
                    Toolbar: (props) => {
                        const count = props.dataManager?.filteredData.length ?? 0;
                        return (
                            <>
                                <MTableToolbar {...props} />
                                <div style={{ marginTop: -50, padding: "8px 16px" }}>
                                    <strong>Search result contains {count} waymarkers</strong>
                                </div>
                            </>
                        )
                    },
                }}
            />
        </div>
    );
}
